import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react'
import { Typography, Box, useTheme, Slide, useMediaQuery } from '@mui/material'
import {
  StickyCompactHeader,
  FullSizedHeader,
  HeaderDetailsContainer,
  StickyCompactHeaderOffset
} from './styles'
import type { CustomBreadcrumbT } from '../../utils/BreadcrumbDataPreparation'
import CustomBreadcrumbs from '../CustomBreadcrumbs'
import BackButton from '../BackButton'
import useAppBarHeight from '../../hooks/useAppBarHeight'
import { capitalizeFirst } from '../../utils/utils'

interface PageHeaderProps {
  breadcrumb?: CustomBreadcrumbT[]
  title: string | undefined
  description?: string | React.ReactNode
  statusChip?: React.ReactNode
  attributes?: React.ReactNode
  controls?: React.ReactNode
  isCollapsible?: boolean
  isCompactOnly?: boolean
  showBackButton?: boolean
  resetState?: () => any
  actions?: React.ReactNode
  pageTitleAriaLabel?: string
}

const PageHeader: React.FC<PageHeaderProps> = ({
  breadcrumb,
  title,
  description,
  statusChip,
  attributes,
  controls,
  isCollapsible = true,
  isCompactOnly = false,
  showBackButton = false,
  resetState,
  actions,
  pageTitleAriaLabel
}) => {
  const theme = useTheme()
  const [isCollapsed, setIsCollapsed] = useState(isCompactOnly)
  const headerRef = useRef<HTMLDivElement>(null)
  const stickyCompactHeaderRef = useRef<HTMLDivElement>(null)
  const [stickyCompactHeaderHeight, setStickyCompactHeaderHeight] = useState(72)
  const appBarHeight = useAppBarHeight()
  const isMobileCompactHeader = useMediaQuery(theme.breakpoints.down('sm'))

  const handleScroll = useCallback((): void => {
    if (isCollapsible && headerRef.current) {
      const threshold = (headerRef.current.offsetHeight + appBarHeight) * 0.8
      setIsCollapsed(window.scrollY > threshold)
    }
  }, [isCollapsible, appBarHeight])

  useEffect(() => {
    if (isCollapsible && !isCompactOnly) {
      window.addEventListener('scroll', handleScroll)
    }
    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [isCollapsible, isCompactOnly, handleScroll])

  const showStickyCompactHeader = isCompactOnly || (isCollapsible && isCollapsed)

  /** Keep flow spacer height in sync with fixed StickyCompactHeader (title + description can exceed default 72px). */
  useLayoutEffect(() => {
    if (!showStickyCompactHeader) return

    const resolveEl = (): HTMLElement | null =>
      stickyCompactHeaderRef.current ??
      document.querySelector<HTMLElement>('[data-sticky-compact-header]')

    const measure = (): void => {
      const el = resolveEl()
      if (el != null) {
        setStickyCompactHeaderHeight(el.offsetHeight)
      }
    }

    measure()

    const el = resolveEl()
    if (el == null) return

    const ro =
      typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(() => {
          measure()
        })
        : null
    ro?.observe(el)

    return () => {
      ro?.disconnect()
    }
  }, [showStickyCompactHeader, title, description, appBarHeight, isMobileCompactHeader, attributes, controls, statusChip])

  return (
    <React.Fragment>
      {
        !isCompactOnly &&
        <Box display={'flex'} gap={theme.spacing(1)} flexDirection={'column'} ref={headerRef}>
          {breadcrumb && <CustomBreadcrumbs breadcrumb={breadcrumb} />}
          {actions && <Box sx={{ mb: theme.spacing(1) }}>{actions}</Box>}
          <FullSizedHeader>
            <HeaderDetailsContainer hasControls={controls != null}>
              <Box display={'flex'} alignItems={'center'} gap={theme.spacing(4)}>
                {showBackButton && <BackButton showLabel={false} resetState={resetState} />}
                <Typography whiteSpace={'nowrap'} aria-label={pageTitleAriaLabel ?? title} variant='h2' marginBottom={description ? theme.spacing(2) : attributes ? theme.spacing(3) : 0}>{capitalizeFirst(title ?? '')}</Typography>
                {statusChip && statusChip}
              </Box>
              {description && (
                React.isValidElement(description)
                  ? description
                  : <Typography variant='body1' marginBottom={theme.spacing(3)}>{description}</Typography>
              )}
              {attributes && attributes}
            </HeaderDetailsContainer>
            {controls && controls}
          </FullSizedHeader>
        </Box>
      }
      {
        isCompactOnly &&
        <StickyCompactHeaderOffset id="Sticky-header-offset" stickyCompactHeaderHeight={stickyCompactHeaderHeight} />
      }
      {
        showStickyCompactHeader &&
        <Slide direction="down" in={true} timeout={300}>
          <StickyCompactHeader ref={stickyCompactHeaderRef} appBarHeight={appBarHeight} data-sticky-compact-header="">
            {isMobileCompactHeader
              ? (
                <Box display="flex" flexDirection="column" width="100%">
                  <Box
                    display="flex"
                    alignItems="flex-start"
                    flexDirection="column"
                    gap={theme.spacing(2)}
                    width="100%"
                  >
                    {showBackButton && <BackButton showLabel={false} resetState={resetState} />}
                    <HeaderDetailsContainer hasControls={controls != null}>
                      <Box display={'flex'} alignItems={'center'} gap={theme.spacing(4)}>
                        <Typography variant='h2' marginBottom={description ? theme.spacing(2) : attributes ? theme.spacing(3) : 0}>{capitalizeFirst(title ?? '')}</Typography>
                        {statusChip && statusChip}
                      </Box>
                      {description && (
                        React.isValidElement(description)
                          ? description
                          : <Typography variant='body1' marginBottom={theme.spacing(3)}>{description}</Typography>
                      )}
                      {attributes && attributes}
                    </HeaderDetailsContainer>
                  </Box>
                  {controls && <Box width="100%">{controls}</Box>}
                </Box>
                )
              : (
                <React.Fragment>
                  <Box
                    display={'flex'}
                    alignItems="center"
                    flexDirection="row"
                    gap={theme.spacing(4)}
                    width="100%"
                  >
                    {showBackButton && <BackButton showLabel={false} resetState={resetState} />}
                    <HeaderDetailsContainer hasControls={controls != null}>
                      <Box display={'flex'} alignItems={'center'} gap={theme.spacing(4)}>
                        <Typography variant='h2' marginBottom={description ? theme.spacing(2) : attributes ? theme.spacing(3) : 0}>{capitalizeFirst(title ?? '')}</Typography>
                        {statusChip && statusChip}
                      </Box>
                      {description && (
                        React.isValidElement(description)
                          ? description
                          : <Typography variant='body1' marginBottom={theme.spacing(3)}>{description}</Typography>
                      )}
                      {attributes && attributes}
                    </HeaderDetailsContainer>
                  </Box>
                  {controls && controls}
                </React.Fragment>
                )}
          </StickyCompactHeader>
        </Slide>
      }
    </React.Fragment>
  )
}

export default PageHeader
